import {
    BaseExportType,
    getClausesByScopeIds,
    IExportBase,
    IScope,
} from "@/api";
import { CheckboxItem } from "@/components/shared/CheckboxItem";
import { Modal } from "@/components/shared/Modal";
import { StyledToggleButtonGroup } from "@/components/shared/StyledToggleButtonGroup";
import { useAuth } from "@/contexts/auth/useAuth";
import {
    Box,
    Button,
    CircularProgress,
    styled,
    ToggleButton,
    Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { trackScopeReportExported } from "@/lib/analytics";

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    fontSize: "0.875rem",
    marginBottom: theme.spacing(1),
}));

const SelectAllButton = styled(Button)(({ theme }) => ({
    fontSize: "0.75rem",
    padding: "4px 12px",
    textTransform: "none",
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: "transparent",
    "&:hover": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderColor: theme.palette.primary.main,
    },
}));

interface ExportScopeModalProps {
    open: boolean;
    onClose: () => void;
    scope: IScope;
    onExport: (data: IExportBase) => void;
}

export const ExportScopeModal: React.FC<ExportScopeModalProps> = ({
    open,
    onClose,
    scope,
    onExport,
}) => {
    const { user } = useAuth();
    const [selectedClauses, setSelectedClauses] = useState<Set<number>>(
        new Set()
    );
    const [includeGuidance, setIncludeGuidance] = useState(true);
    const [includeComments, setIncludeComments] = useState(true);
    const [includeMetadata, setIncludeMetadata] = useState(false);
    const [exportType, setExportType] = useState<BaseExportType>(
        BaseExportType.XLSX
    );

    const { data: clauses, isLoading } = useQuery({
        queryKey: ["frontend-clauses", [scope.id]],
        queryFn: () => getClausesByScopeIds([scope.id]),
        enabled: open,
    });

    useEffect(() => {
        if (open) {
            setSelectedClauses(new Set());
        }
    }, [open]);

    const handleSelectAll = () => {
        if (clauses && selectedClauses.size === clauses.length) {
            setSelectedClauses(new Set());
        } else if (clauses) {
            setSelectedClauses(new Set(clauses.map((clause) => clause.id)));
        }
    };

    const handleClauseToggle = (clauseId: number) => {
        const newSelected = new Set(selectedClauses);
        if (newSelected.has(clauseId)) {
            newSelected.delete(clauseId);
        } else {
            newSelected.add(clauseId);
        }
        setSelectedClauses(newSelected);
    };

    const handleExport = () => {
        onExport({
            clauses: Array.from(selectedClauses),
            include_guidance: includeGuidance,
            include_comments: includeComments,
            include_metadata: includeMetadata,
            include_fields: true, // Default to true for single scope exports
            export_type: exportType,
        });
        trackScopeReportExported({
            scope_id: scope.id,
            export_type: exportType,
            clause_count: selectedClauses.size,
            include_guidance: includeGuidance,
            include_comments: includeComments,
            include_metadata: includeMetadata,
        });
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Export"
            maxWidth="sm"
            actions={[
                {
                    label: "Export Selected",
                    onClick: handleExport,
                    color: "primary",
                    disabled: selectedClauses.size === 0,
                },
            ]}
            footerText={`${selectedClauses.size} clauses selected`}
        >
            <Box>
                {/* Clauses Section */}
                <Box sx={{ mb: 3 }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                        }}
                    >
                        <SectionTitle>
                            Clauses ({clauses?.length || 0} items)
                        </SectionTitle>
                        <SelectAllButton onClick={handleSelectAll} size="small">
                            {clauses && selectedClauses.size === clauses.length
                                ? "Deselect All"
                                : "Select All"}
                        </SelectAllButton>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        {isLoading ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    p: 3,
                                }}
                            >
                                <CircularProgress size={24} />
                            </Box>
                        ) : !clauses || clauses.length === 0 ? (
                            <Box sx={{ p: 3, textAlign: "center" }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    No clauses found
                                </Typography>
                            </Box>
                        ) : (
                            clauses.map((clause) => (
                                <CheckboxItem
                                    key={clause.id}
                                    checked={selectedClauses.has(clause.id)}
                                    onChange={() =>
                                        handleClauseToggle(clause.id)
                                    }
                                    title={clause.title}
                                />
                            ))
                        )}
                    </Box>
                </Box>

                {/* Additional Options Section */}
                <Box sx={{ mb: 3 }}>
                    <SectionTitle>Additional Options</SectionTitle>

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        <CheckboxItem
                            checked={includeGuidance}
                            onChange={setIncludeGuidance}
                            title="Guidance"
                            description="Add expert recommendations and guidance for each extracted item"
                        />

                        <CheckboxItem
                            checked={includeComments}
                            onChange={setIncludeComments}
                            title="Comments"
                            description="Include user-added comments and annotations for each clause"
                        />

                        {user?.is_admin && (
                            <CheckboxItem
                                checked={includeMetadata}
                                onChange={setIncludeMetadata}
                                title="Metadata"
                                description="Include metadata in export (reasoning, confidence)"
                            />
                        )}
                    </Box>
                </Box>

                {/* Export Format Section */}
                <Box>
                    <SectionTitle>Export Format</SectionTitle>
                    <StyledToggleButtonGroup
                        value={exportType}
                        exclusive
                        onChange={(_e, newValue) => {
                            if (newValue !== null) {
                                setExportType(newValue);
                            }
                        }}
                        size="small"
                    >
                        <ToggleButton value={BaseExportType.XLSX}>
                            .xlsx
                        </ToggleButton>
                    </StyledToggleButtonGroup>
                </Box>
            </Box>
        </Modal>
    );
};
